import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/api/endpoints';
import type { AdminMatchday, MatchdayKind } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { formatDateTime, isoToLocalInput, localInputToIso } from '@/lib/datetime';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

const KIND_OPTIONS: { value: MatchdayKind; label: string }[] = [
  { value: 'liga', label: '3. Liga' },
  { value: 'pokal', label: 'DFB-Pokal' },
  { value: 'bonus', label: 'Bonus (Fragen)' },
];

export function AdminMatchdaysPage() {
  const { data, loading, error, reload } = useAsync<AdminMatchday[]>(
    (signal) => adminApi.listMatchdays(signal),
    [],
  );

  const matchdays = [...(data ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <>
      <CreateMatchdayForm onCreated={reload} />

      {loading && <Spinner label="Spieltage werden geladen…" />}
      {error && <Alert>{error}</Alert>}

      {!loading && !error && matchdays.length === 0 && (
        <Card>
          <p className="muted">
            Noch keine Spieltage. Lege oben einen an oder stoße einen Sync an.
          </p>
        </Card>
      )}

      {matchdays.map((md) => (
        <AdminMatchdayCard key={md.id} matchday={md} onChanged={reload} />
      ))}
    </>
  );
}

function CreateMatchdayForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<MatchdayKind>('liga');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const created = await adminApi.createMatchday({
        name: name.trim(),
        kind,
        deadline_at: deadline ? localInputToIso(deadline) : null,
      });
      setSaved(`Spieltag „${created.name}" angelegt.`);
      setName('');
      setDeadline('');
      setKind('liga');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Spieltag anlegen</h2>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 180px' }}>
          <span className={styles.label}>Name</span>
          <input
            className={styles.input}
            value={name}
            placeholder="z. B. 12. Spieltag"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0 }}>
          <span className={styles.label}>Wettbewerb</span>
          <select
            className={styles.input}
            value={kind}
            onChange={(e) => setKind(e.target.value as MatchdayKind)}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field} style={{ marginBottom: 0 }}>
          <span className={styles.label}>Tippschluss (optional)</span>
          <input
            type="datetime-local"
            className={styles.deadlineInput}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </label>
        <Button
          className={styles.btnSm}
          disabled={busy || name.trim() === ''}
          onClick={() => void submit()}
        >
          {busy ? '…' : 'Anlegen'}
        </Button>
      </div>
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

function AdminMatchdayCard({
  matchday,
  onChanged,
}: {
  matchday: AdminMatchday;
  onChanged: () => void;
}) {
  const [deadline, setDeadline] = useState(isoToLocalInput(matchday.deadline_at));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function patch(input: Parameters<typeof adminApi.patchMatchday>[1], successMsg: string) {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.patchMatchday(matchday.id, input);
      setSaved(successMsg);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className={styles.row}>
        <div className={styles.rowMain}>
          <div className={styles.rowTitle}>{matchday.name}</div>
          <div className={styles.counts}>
            <span>{matchday.match_count} Spiele</span>
            <span>{matchday.predictions_count} Tipps</span>
            <span
              className={
                matchday.is_published
                  ? `${styles.statusBadge} ${styles.statusActive}`
                  : `${styles.statusBadge} ${styles.statusPending}`
              }
            >
              {matchday.is_published ? 'veröffentlicht' : 'Entwurf'}
            </span>
            {matchday.deadline_overridden && (
              <span className={`${styles.statusBadge} ${styles.statusPending}`}>
                Deadline manuell
              </span>
            )}
          </div>
          <div className="muted" style={{ marginTop: 4 }}>
            Tippschluss: {formatDateTime(matchday.deadline_at)}
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            className={styles.btnSm}
            disabled={busy}
            onClick={() =>
              void patch(
                { is_published: !matchday.is_published },
                matchday.is_published ? 'Zurückgezogen.' : 'Veröffentlicht.',
              )
            }
          >
            {matchday.is_published ? 'Zurückziehen' : 'Veröffentlichen'}
          </Button>
        </div>
      </div>

      <div className={styles.deadlineForm}>
        <label className={styles.field} style={{ marginBottom: 0 }}>
          <span className={styles.label}>Tippschluss überschreiben</span>
          <input
            type="datetime-local"
            className={styles.deadlineInput}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </label>
        <Button
          className={styles.btnSm}
          disabled={busy || deadline === ''}
          onClick={() => {
            const iso = localInputToIso(deadline);
            if (iso) void patch({ deadline_at: iso }, 'Deadline gesetzt.');
          }}
        >
          Deadline setzen
        </Button>
      </div>

      <div className={styles.mdLinks}>
        {matchday.kind !== 'bonus' && (
          <>
            <Link to={`/admin/matchdays/${matchday.id}/matches`}>Spiele verwalten →</Link>
            <Link to={`/admin/matchdays/${matchday.id}/results`}>Ergebnisse eingeben →</Link>
          </>
        )}
        <Link to={`/admin/matchdays/${matchday.id}/questions`}>
          {matchday.kind === 'bonus' ? 'Bonusfragen verwalten →' : 'Fragen verwalten →'}
        </Link>
      </div>

      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}
